import { getDeepDiff } from "@/utilities/deep-diff";
import { Monitoring } from "@/utilities/monitoring";
import { safeStringify } from "@/utilities/safe-stringify";
import type { Store } from "vuex";
import { toRaw } from "vue";

/**
 * Vuex plugin for tracing actions and mutations with Sentry-style monitoring.
 *
 * This plugin instruments Vuex actions and mutations to provide detailed tracing and breadcrumbs
 * for debugging and performance monitoring. It starts and ends monitoring spans for actions,
 * logs errors, and records mutation breadcrumbs with deep-diff state changes.
 *
 * @param store - The Vuex store instance to instrument.
 *
 * @remarks
 * - Uses `Monitoring` utility for span and breadcrumb management.
 * - Captures action lifecycle events (`before`, `after`, `error`).
 * - Records mutation payloads and state changes asynchronously.
 */
export default function sentryVuexTracer(store: Store<unknown>): void {

    let oldState = safeStringify(toRaw(store.state));

    store.subscribeAction({
        before: (action, state) => {
            Monitoring.startSpan(action, "vuex.action", action.type, action);
        },
        after: (action, state) => {
            Monitoring.endSpan(action);
        },
        error: (action, state, error) => {
            Monitoring.errorSpan(action);
        }
    });

    store.subscribe((mutation, state) => {
        const oldStateSnapshot = oldState;
        const rawState = safeStringify(toRaw(state));
        oldState = rawState;

        setTimeout(() => {
            const changes = getDeepDiff(JSON.parse(oldStateSnapshot), JSON.parse(rawState));
            Monitoring.addBreadcrumb(`mutation: ${mutation.type}`, {
                payload: mutation.payload,
                changes
            }, "vuex.mutation");
        }, 0); // requestIdleCallback less blocking, but could create breadcrumbs too late
    });
}